import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../../hooks/useStore';
import { DataGrid, DataGridElement } from '../../../../../lib/components/compound/dataGrid';
import { useQuery } from '@tanstack/react-query';
import { Box, Grid, Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { getPersonioCheckQueryOptions } from '../../../../../queries/personio';
import { useCompanySettings } from '../../../../../queries/company';
import {
  useFetchPaychexApiCredentials,
  useFetchPaychexCompanies
} from '../../../../../queries/company/paychex';

interface SyncStatus {
  status: 'success' | 'error';
  message?: string;
}

export const PayrollSystemView = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();

  const { enqueueSnackbar } = useSnackbar();

  const { data: companySettings } = useCompanySettings(companyStore.company?.id ?? '');

  const queryOptions = getPersonioCheckQueryOptions(
    companyStore.company?.id ?? '',
    (data) => {
      const statusData = data as SyncStatus;
      let messageKey = 'common:CONNECTION_FAILED';

      if (statusData.status === 'success') {
        messageKey = 'company:PERSONIO_APP_CONNECTED';
      } else if (statusData.message === 'No Personio App found for this company') {
        messageKey = 'company:PERSONIO_APP_NOT_FOUND';
      } else if (statusData.message === 'Multiple Personio apps found for this company') {
        messageKey = 'company:PERSONIO_MULTIPLE_APPS_FOUND';
      } else if (statusData.message === 'Access token is not valid') {
        messageKey = 'company:PERSONIO_INVALID_TOKEN';
      }

      enqueueSnackbar(t(messageKey), { variant: statusData.status });
    },
    () => {
      enqueueSnackbar(t('common:CONNECTION_FAILED'), { variant: 'error' });
    }
  );

  const {
    data: personioStatus,
    isPending,
    isRefetching
  } = useQuery({
    ...queryOptions,
    enabled: companySettings?.payroll_system === 'PERSONIO',
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
  const { data: paychexApiCredentials } = useFetchPaychexApiCredentials(
    companyStore.company?.id ?? ''
  );
  const { data: paychexCompanies } = useFetchPaychexCompanies(companyStore.company?.id ?? '', {
    enabled:
      companySettings?.payroll_system === 'PAYCHEX_PLUS' &&
      !!paychexApiCredentials?.paychex_company_id
  });

  return (
    <Box display="flex" flexDirection="column">
      <DataGrid>
        <DataGridElement
          xs={3}
          title={t('company:PAYROLL_SYSTEM')}
          value={t('company:PAYROLL_SYSTEM', { context: companySettings?.payroll_system })}
        />
        <>
          {companySettings?.payroll_system?.startsWith('DATEV') && (
            <>
              <DataGridElement
                xs={3}
                title={t('company:DATEV_CONSULTANT_NO')}
                value={companySettings?.datev_consultant_number}
              />
              <DataGridElement
                xs={3}
                title={t('company:DATEV_CLIENT_NO')}
                value={companySettings?.datev_client_number}
              />
              {companySettings?.payroll_system === 'DATEV_LODAS' && (
                <DataGridElement
                  xs={3}
                  title={t('company:DATEV_PROCESSING_KEY')}
                  value={companySettings?.datev_processing_key as string}
                />
              )}
            </>
          )}
          {companySettings?.payroll_system == 'AGENDA' && (
            <DataGridElement
              xs={3}
              title={t('company:AGENDA_CONSULTANT_NO')}
              value={companySettings?.agenda_client_number}
            />
          )}
          {companySettings?.payroll_system == 'SAGE' && (
            <DataGridElement
              xs={3}
              title={t('company:SAGE_CLIENT_NO')}
              value={companySettings?.sage_client_number}
            />
          )}
          {companySettings?.payroll_system == 'PERSONIO' && (
            <>
              {isPending || isRefetching ? (
                <Grid item xs={3}>
                  <Skeleton variant="rounded" height="100%" sx={{ borderRadius: '0.75rem' }} />
                </Grid>
              ) : (
                <DataGridElement
                  xs={6}
                  title={t('company:STATUS')}
                  sx={(theme) => ({
                    '& .MuiBox-root': {
                      backgroundColor: `${personioStatus?.status === 'success' ? theme.palette.success.light : theme.palette.error.light}`
                    }
                  })}
                  value={
                    personioStatus?.status === 'success'
                      ? t('common:CONNECTION_SUCCESS')
                      : t('common:CONNECTION_FAILED')
                  }
                />
              )}
            </>
          )}
          {companySettings?.payroll_system == 'PAYCHEX_PLUS' && (
            <>
              <DataGridElement
                xs={3}
                title={t('paychex:paychexApiUrl')}
                value={paychexApiCredentials?.api_url}
              />
              <DataGridElement xs={3} title={t('paychex:paychexApiKey')} value={'************'} />
              <DataGridElement
                xs={3}
                title={t('common:COMPANY')}
                value={
                  paychexCompanies?.companies.find(
                    (company) => company.id === paychexApiCredentials?.paychex_company_id
                  )?.name
                }
              />
            </>
          )}
          {companySettings?.payroll_system == 'ADDISON' && (
            <DataGridElement
              title={t('company:ADDISON_CLIENT_NO')}
              xs={4}
              value={companySettings?.addison_client_number as string}
            />
          )}
        </>
      </DataGrid>
    </Box>
  );
});
